import { RelativeDateDisplayMetric } from '@pushly/models/lib/enums/relative-date-display-metric'
import { DomainDto } from '../../dtos/domain'
import { IPageAnalysisContentAgeLimit, IPageAnalysisUrlRule } from '../../interfaces/page-analysis-gates'
import {
    PageAnalysisRuleState,
    PageAnalysisScope,
    UnknownContentAgePolicy,
    UrlRuleMatchType,
    UrlRulePolicy,
} from '@pushly/models/lib/enums'

export enum AiView {
    OVERVIEW = 'overview',
    BRAND_VOICE = 'brand-voice',
    CURATOR = 'curator',
    PAGE_ANALYSIS = 'page-analysis',
}

export const DEFAULT_AI_VIEW = AiView.OVERVIEW

export const AI_VIEW_LABELS: Record<AiView.OVERVIEW | AiView.BRAND_VOICE, string> = {
    [AiView.OVERVIEW]: 'Overview',
    [AiView.BRAND_VOICE]: 'Brand Voice',
    // [AiView.CURATOR]: 'Curator',
    // [AiView.PAGE_ANALYSIS]: 'Page Analysis',
}

export const isAiView = (value: string): value is AiView => (Object.values(AiView) as string[]).includes(value)

/**
 * Nothing stored reaches the screen unlabelled. Both gates use the same words
 * for the same concept, so a reader never has to learn that a rule's `scopes`
 * and a limit's `scope` mean the same thing.
 */
export const SCOPE_LABELS: Record<PageAnalysisScope, string> = {
    [PageAnalysisScope.ETL]: 'Background traffic',
    [PageAnalysisScope.DIRECT]: 'On-demand',
}

/** Said once on the Curator view, so the two wells below it can stay specific. */
export const CURATOR_DESCRIPTION =
    'Every audience gets the story it is most likely to open, chosen from your own library and scored against ' +
    'what that segment has actually clicked. Set it once on a journey step and each send picks itself, so one ' +
    'campaign lands differently for readers with different interests.'

export const HEALTH_DESCRIPTION =
    'How much of this domain the Curator can actually draw on: which segments have enough click history to be ' +
    'scored against, and how much of the library carries the embeddings it ranks with.'

/**
 * These live on the journey step today, not on the domain, so the well reports
 * them and says where to change them.
 */
export const STEP_DEFAULTS_DESCRIPTION =
    'What a new Curator step starts from. Each step can widen any of these for itself, and changing one there ' +
    'does not affect steps that are already running.'

/**
 * Enrichment Gates is our term, so the well says what one does before it lists
 * any. What a gate declines is the language model step and nothing else.
 */
export const GATES_DESCRIPTION =
    'Gates decide which pages get the full analysis. A page that does not pass is still fetched, and still ' +
    'contributes its title, description, image and keywords. Only the language model step is skipped.'

/** What each traffic path is, and what a rule on it affects. */
export const SCOPE_DESCRIPTIONS: Record<PageAnalysisScope, string> = {
    [PageAnalysisScope.DIRECT]:
        'Analysis asked for by name, such as the landing page of a notification someone is writing. A person is ' +
        'waiting on the result, so a rule that declines here is felt immediately.',
    [PageAnalysisScope.ETL]:
        'Analysis triggered by traffic, when a subscriber opens a page and it joins the queue. Nothing is waiting ' +
        'on the result, so rules here shape what reaches the content library over time.',
}

/**
 * Trace is drawn as its own state, never as a faded Active: a rule that has
 * been reporting for a month with a stable count is doing its job, not waiting
 * to be finished.
 */
export const RULE_STATE_LABELS: Record<PageAnalysisRuleState, string> = {
    [PageAnalysisRuleState.DISABLED]: 'Off',
    [PageAnalysisRuleState.TRACE]: 'Reporting only',
    [PageAnalysisRuleState.ACTIVE]: 'Enforced',
}

/**
 * The compact form, for the state control that sits inline in a section row
 * next to the sentence it governs. `Reporting only` does not fit there.
 */
export const RULE_STATE_SHORT_LABELS: Record<PageAnalysisRuleState, string> = {
    [PageAnalysisRuleState.DISABLED]: 'Off',
    [PageAnalysisRuleState.TRACE]: 'Reporting',
    [PageAnalysisRuleState.ACTIVE]: 'Enforced',
}

export const RULE_STATE_DESCRIPTIONS: Record<PageAnalysisRuleState, string> = {
    [PageAnalysisRuleState.DISABLED]: 'Not evaluated. The rule is kept but does nothing.',
    [PageAnalysisRuleState.TRACE]: 'Counted, never acted on. Leave a rule here until the numbers stop surprising you.',
    [PageAnalysisRuleState.ACTIVE]: 'Acted on. Matching pages stop being enriched as soon as this is applied.',
}

export const MATCH_TYPE_LABELS: Record<UrlRuleMatchType, string> = {
    [UrlRuleMatchType.HOST_EQUALS]: 'Site is',
    [UrlRuleMatchType.PATH_PREFIX]: 'Path starts with',
    [UrlRuleMatchType.PATH_CONTAINS]: 'Path contains',
    [UrlRuleMatchType.PATH_SUFFIX]: 'Path ends with',
}

/**
 * The same choice as a sentence fragment. `MATCH_TYPE_LABELS` reads as a form
 * label above a field; a rule read back in prose needs the relative clause.
 */
export const MATCH_TYPE_CLAUSES: Record<UrlRuleMatchType, string> = {
    [UrlRuleMatchType.HOST_EQUALS]: 'served from the site',
    [UrlRuleMatchType.PATH_PREFIX]: 'whose path starts with',
    [UrlRuleMatchType.PATH_CONTAINS]: 'whose path contains',
    [UrlRuleMatchType.PATH_SUFFIX]: 'whose path ends with',
}

export const POLICY_LABELS: Record<UrlRulePolicy, string> = {
    [UrlRulePolicy.ALLOW]: 'Only analyze',
    [UrlRulePolicy.DENY]: 'Never analyze',
}

export const UNKNOWN_AGE_POLICY_LABELS: Record<UnknownContentAgePolicy, string> = {
    [UnknownContentAgePolicy.ANALYZE]: 'Analyze anyway',
    [UnknownContentAgePolicy.DENY]: 'Decline',
}

/** The same choice as a clause, for reading a saved limit back as a sentence. */
const UNKNOWN_AGE_POLICY_CLAUSES: Record<UnknownContentAgePolicy, string> = {
    [UnknownContentAgePolicy.ANALYZE]: 'Pages with no publication date are analyzed anyway.',
    [UnknownContentAgePolicy.DENY]: 'Pages with no publication date are declined.',
}

/**
 * Each service is gated separately, and FEAT_PUSHLY_AI additionally gates the
 * suite: without it there is nothing here a customer can configure.
 */
export const domainHasFlag = (domain: DomainDto | undefined, flag: string): boolean =>
    domain?.flags?.includes(flag) ?? false

/**
 * The metrics an age limit can be authored in, and what each is worth. The
 * drawer multiplies by this to store the interval and `formatMaxAge` divides
 * by it to read one back, so both directions have to come from here.
 */
export const SECONDS_PER_METRIC: Partial<Record<RelativeDateDisplayMetric, number>> = {
    [RelativeDateDisplayMetric.HOURS]: 3600,
    [RelativeDateDisplayMetric.DAYS]: 86400,
    [RelativeDateDisplayMetric.MONTHS]: 2592000,
}

/**
 * The stored interval governs; the metric records the unit it was authored in.
 * Rendering everything in days turns `12 hours` into `0.5 days`.
 */
export const formatMaxAge = (seconds: number, metric: RelativeDateDisplayMetric): string => {
    const perUnit = SECONDS_PER_METRIC[metric] ?? SECONDS_PER_METRIC[RelativeDateDisplayMetric.DAYS]!
    const amount = Math.round((seconds / perUnit) * 10) / 10
    const unit = amount === 1 ? metric.replace(/s$/, '') : metric

    return `${amount} ${unit}`
}

/**
 * A saved limit read back as prose. The two settings are one decision, so the
 * section states them as one sentence rather than as a row of labelled values.
 */
/**
 * A rule read back as prose, the way the table states it. The stored policy and
 * match type are shown separately, so this is the meaning rather than the values.
 */
export const ruleSentence = (rule: IPageAnalysisUrlRule): string =>
    `${POLICY_LABELS[rule.policy]} pages ${MATCH_TYPE_CLAUSES[rule.matchType]} ${rule.value}`

/**
 * Which traffic a rule governs, short enough for a table cell. A rule carries a
 * list, so the cell names the combination rather than one scope.
 */
export const scopeTagLabel = (scopes: PageAnalysisScope[]): string => {
    if (scopes.length > 1) {
        return 'Both paths'
    }

    if (scopes.length === 0) {
        return 'Neither path'
    }

    return scopes[0] === PageAnalysisScope.ETL ? 'Background' : 'On-demand'
}

export const ageLimitSentence = (limit: IPageAnalysisContentAgeLimit): string =>
    `Never analyze articles older than ${formatMaxAge(limit.maxAgeSeconds, limit.maxAgeDisplayMetric)}. ` +
    UNKNOWN_AGE_POLICY_CLAUSES[limit.unknownPolicy]
